import {
  Button,
  FormControl,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Skeleton,
  Select,
  MenuItem,
} from '@mui/material'
import {
  CameraAlt,
  FlipCameraIos,
  RestartAlt,
  Analytics,
} from '@mui/icons-material'
import classNames from 'classnames'
import { Fragment } from 'react'

import useAnalyzeTrayLogic from './AnalyzeTray.logic'
import { useAddTrayContext } from '../AddTray.context'
import type { TrayType } from 'components/organisms/EditTrayModal/EditTrayModal.types'

import { LoadingButton } from 'components/mui'
import DialogDrawer from 'components/molecules/Drawer/DialogDrawer'
import TrayAnalysisContent from 'components/organisms/TrayAnalysisContent/TrayAnalysisContent'
import AnalysisErrorModal from 'components/organisms/AnalysisErrorModal/AnalysisErrorModal'
import './AnalyzeTray.scss'
import { OtherTrayItemsProvider } from 'components/organisms/OtherTrayItems'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

const AnalyzeTray = () => {
  const {
    handleImageCapture,
    videoRef,
    cameras,
    description,
    handleDescriptionChange,
    activeCamera,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    isTrayMapDrawerOpen,
    handleTrayMapDrawerClose,
    isSubmitting,
    handleAnalysisClick,
    handleBackClick,
    hasPlates,
    parLevel,
    handleTrayParLevelChange,
    isInstrumentTray,
    recapture,
    currentTrayTypes,
    isErrorModalOpen,
    handleRetryCapture,
    handleProceedManually,
    handleConfirm,
    hasAnalyzed,
    otherItems,
    setOtherItems,
    isCustomImplantTray,
    handleTrayTypeSelection,
    traysForSelectedSet,
    renderTrayLabel,
    needsRotate,
  } = useAnalyzeTrayLogic()

  /* shared state pulled from context */
  const {
    trayImage,
    isAnalysisComplete,
    selectedTrayType,
    setSelectedTrayType,
    selectedTraySetName,
  } = useAddTrayContext()

  // Use the hook to get the presigned URL for the tray image
  const { imageUrl: trayImageSrc, isLoading: isTrayImageLoading } =
    useDTMS3Image({
      url: trayImage?.src,
      defaultImage: '',
    })

  return (
    <OtherTrayItemsProvider items={otherItems} setItems={setOtherItems}>
      <Paper className="analyze-tray-step step-item">
        {/* TOP - Centered dropdown */}
        <div className="tray-type-dropdown-container">
          <div className="tray-type-dropdown-wrapper">
            <Select
              fullWidth
              variant="outlined"
              displayEmpty
              value={selectedTrayType || ''}
              onChange={(e) => {
                const trayTypeValue = e.target.value as TrayType
                setSelectedTrayType(trayTypeValue)
                if (trayTypeValue) {
                  handleTrayTypeSelection(trayTypeValue)
                }
              }}
              renderValue={(selected) => {
                if (!selected) return 'Select Tray Type'
                const selectedTray = traysForSelectedSet.find(
                  (tray) => tray.trayType === selected
                )
                return selectedTray ? renderTrayLabel(selectedTray) : selected
              }}
              className="tray-type-select"
            >
              <MenuItem disabled value="">
                Select Tray Type
              </MenuItem>
              {traysForSelectedSet.map((tray: any) => (
                <MenuItem key={tray.trayType} value={tray.trayType}>
                  <div className="tray-menu-item">
                    <Typography variant="body2" className="tray-label">
                      {renderTrayLabel(tray)}
                    </Typography>
                    <img
                      src={tray.imageUrl}
                      alt={tray.trayType}
                      className="tray-thumbnail"
                    />
                  </div>
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* BOTTOM - Camera and form section */}
        <div className="analyze-tray">
          {/* everything below is identical to the pre-list version */}
          <div className="camera-form-section">
            {/*  CAMERA / IMAGE AREA  */}
            <div className="camera-section">
              <div className="camera-container">
                {(!selectedTrayType || isSubmitting) && (
                  <div className="tray-type-overlay">
                    <Typography
                      variant="h5"
                      className={classNames(
                        'overlay-text',
                        isSubmitting ? '' : 'warning'
                      )}
                    >
                      {isSubmitting
                        ? 'Analyzing tray...'
                        : 'Please select a tray type'}
                    </Typography>
                  </div>
                )}
                {!trayImage && (
                  <>
                    {isCameraMenuOpen && (
                      <div className="camera-options">
                        {cameras.map((camera) => (
                          <div
                            key={camera.deviceId}
                            className={classNames('camera-option', {
                              active:
                                camera.deviceId === activeCamera?.deviceId,
                            })}
                            onClick={() => handleSelectCamera(camera)}
                          >
                            <Typography className="label">
                              {camera.label}
                            </Typography>
                            <Typography className="type">
                              {camera?.cameraType}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    )}
                    <LoadingButton
                      variant="contained"
                      loading={false}
                      onClick={handleToggleCameraMenu}
                      startIcon={<FlipCameraIos />}
                      className="camera-switch"
                    >
                      Switch Camera
                    </LoadingButton>
                    <div className="capture-buttons">
                      <LoadingButton
                        className="capture"
                        onClick={() => handleImageCapture(true)}
                        loading={false}
                        variant="text"
                      >
                        <CameraAlt className="icon" />
                      </LoadingButton>
                    </div>
                  </>
                )}
                {trayImage ? (
                  <div className="image-container">
                    {isTrayImageLoading ? (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        animation="wave"
                        className="tray-image-skeleton"
                      />
                    ) : (
                      <img
                        src={trayImageSrc}
                        alt="Tray"
                        className="tray-image"
                      />
                    )}
                    {trayImage.isUploading && (
                      <div className="upload-overlay">
                        <div className="upload-status">
                          <CircularProgress size={48} color="primary" />
                          <Typography variant="h6" className="upload-text">
                            Uploading Image...
                          </Typography>
                          <Typography
                            variant="body2"
                            className="upload-subtext"
                          >
                            Please wait while we process your image
                          </Typography>
                        </div>
                      </div>
                    )}
                    {isSubmitting && !trayImage.isUploading && (
                      <div className="analysis-overlay">
                        <div className="analysis-status">
                          <CircularProgress size={48} color="primary" />
                          <Typography variant="h6" className="analysis-text">
                            Analyzing Tray...
                          </Typography>
                          <Typography
                            variant="body2"
                            className="analysis-subtext"
                          >
                            Our AI is identifying hardware in your tray
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="camera-feed"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                )}
              </div>
              {trayImage && (
                <div className="action-buttons">
                  <Button
                    onClick={recapture}
                    className="recapture-btn"
                    disabled={
                      isSubmitting ||
                      (trayImage && trayImage.isUploading) ||
                      isTrayImageLoading
                    }
                  >
                    <RestartAlt sx={{ mr: 0.5 }} />
                    Recapture
                  </Button>
                  {isSubmitting && !trayImage.isUploading && (
                    <div className="analysis-status-indicator">
                      <div className="spinner-small" />
                      <Typography variant="body2">Analyzing...</Typography>
                    </div>
                  )}
                </div>
              )}
              {(hasPlates || !isInstrumentTray) &&
                !isCustomImplantTray &&
                !(selectedTrayType && parLevel === 1) && (
                  <FormControl className="par-level_container" fullWidth>
                    <Typography
                      component="label"
                      mb={2}
                      className="par-level_label"
                    >
                      Tray Par Level
                    </Typography>
                    <TextField
                      autoComplete="off"
                      placeholder="Tray Par Level"
                      fullWidth
                      type="Number"
                      value={parLevel}
                      onChange={handleTrayParLevelChange}
                      variant="outlined"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 100,
                        },
                      }}
                    />
                  </FormControl>
                )}
              <TextField
                autoComplete="off"
                placeholder={`You must enter tray details, then click ${
                  isCustomImplantTray ? 'Load Tray' : 'Analyze Tray'
                } to continue`}
                fullWidth
                multiline
                minRows={4}
                maxRows={7}
                type="text"
                value={description}
                onChange={handleDescriptionChange}
                variant="outlined"
              />
              {!hasAnalyzed && !isInstrumentTray ? (
                <LoadingButton
                  variant="contained"
                  onClick={handleAnalysisClick}
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    (trayImage && trayImage.isUploading) ||
                    !description ||
                    isTrayImageLoading ||
                    !trayImage
                  }
                  className="analyze-btn"
                  startIcon={isAnalysisComplete ? null : <Analytics />}
                >
                  {isAnalysisComplete
                    ? 'View Analysis'
                    : isCustomImplantTray
                    ? 'Load Tray'
                    : 'Analyze Tray'}
                </LoadingButton>
              ) : (
                <LoadingButton
                  variant="contained"
                  onClick={handleConfirm}
                  loading={isSubmitting}
                  disabled={
                    !trayImage ||
                    (trayImage && trayImage.isUploading) ||
                    isSubmitting ||
                    !description ||
                    isTrayImageLoading
                  }
                  className="confirm-btn"
                >
                  Confirm
                </LoadingButton>
              )}
            </div>
          </div>
        </div>
      </Paper>
      <div className="buttons_container">
        <Button variant="outlined" onClick={handleBackClick}>
          Back
        </Button>
      </div>

      <DialogDrawer
        open={isTrayMapDrawerOpen}
        onClose={handleTrayMapDrawerClose}
        height="95%"
        overflowY
        dismissable={false}
        modal={false}
      >
        <TrayAnalysisContent
          trayType={selectedTrayType}
          onClose={handleTrayMapDrawerClose}
          onConfirm={handleConfirm}
          trayImg={trayImageSrc}
          selectedTrayType={selectedTrayType}
        />
      </DialogDrawer>

      <AnalysisErrorModal
        open={isErrorModalOpen}
        onRetry={handleRetryCapture}
        onProceedManually={handleProceedManually}
      />
    </OtherTrayItemsProvider>
  )
}

export default AnalyzeTray
